import { Box, Card, Typography, Button, CircularProgress, Stack } from "@mui/material";
import { Content } from "@/types";
import { getThumbnailImage } from "@/utils/media";
import { useAppSelector } from "@/hooks";
import RewardPointsChip from "./RewardPointsChip";

interface Props {
  reward: Content;
  availablePoints: number;
  onRedeem: (reward: Content) => void;
  readOnly?: boolean;
}

const RewardCard = ({ reward, availablePoints, onRedeem, readOnly }: Props) => {
  const organizationId = useAppSelector((store) => store.ui.activeOrganization);
  const requiredPoints = reward.loyalty?.pointsCost || 0;
  const progress = Math.min((availablePoints / requiredPoints) * 100, 100);
  const isRedeemable = availablePoints >= requiredPoints;
  const neededPoints = Math.max(requiredPoints - availablePoints, 0);

  const imageUrl = getThumbnailImage({
    file: reward.images,
    organizationId,
    size: "800x500",
  });

  const pluralize = (count: number, singular: string, plural: string) =>
    count === 1 ? singular : plural;

  const handleAction = () => {
    if (isRedeemable) {
      onRedeem(reward);
    }
  };

  return (
    <Card
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        justifyContent: "space-between",
      }}
    >
      <Stack spacing={2} alignItems="center" width="100%">
        <Box sx={{ position: "relative", display: "inline-flex" }}>

          {/* Background circle for progress track */}
          <CircularProgress
            variant="determinate"
            value={100}
            size={120}
            thickness={1.5}
            sx={{
              color: "grey.100",
              position: "absolute",
              left: 0,
            }}
          />
          <CircularProgress
            variant="determinate"
            value={progress}
            size={120}
            thickness={1.5}
            sx={{
              color: isRedeemable ? "success.main" : "primary.main",
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
              },
            }}
          />
          <Box
            sx={{
              top: 10,
              left: 10,
              bottom: 10,
              right: 10,
              position: "absolute",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                backgroundImage: `url(${imageUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          </Box>
        </Box>

        {/* Title */}
        <Typography variant="h3" fontWeight={600} color="textPrimary">
          {reward.title}
        </Typography>

        {/* Points Chip */}
        <RewardPointsChip points={requiredPoints} />
      </Stack>

      {/* Action Button */}
      <Box sx={{ mt: 3, width: "100%" }}>
        {isRedeemable ? (
          <Button
            fullWidth
            variant="contained"
            color="success"
            size="small"
            onClick={!readOnly ? handleAction : undefined}
            sx={{
              pointerEvents: readOnly ? "none" : "auto",
            }}
          >
            {readOnly ? "Listo para canjear" : "Canjear"}
          </Button>
        ) : (
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            size="small"
            onClick={!readOnly ? handleAction : undefined}
            sx={{
              pointerEvents: readOnly ? "none" : "auto",
            }}
          >
            Faltan {neededPoints} {pluralize(neededPoints, "pt", "pts")}
          </Button>
        )}
      </Box>
    </Card>
  );
};

export default RewardCard;
